import React, { useMemo, useRef, useEffect } from "react";
import dateFormat, { i18n } from "dateformat";
import { format } from "date-fns";
import { dayNamesForRent } from "@/resources/locale";
import styles from "./WeekDays.module.scss";

i18n.dayNames = dayNamesForRent;

interface WeekDaysProps {
  date: string;
  onDayClick: (dateString: string) => void;
  selectedDate?: string; // Явно переданная выбранная дата
}

export const WeekDays: React.FC<WeekDaysProps> = ({ date, onDayClick, selectedDate }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);

  // Используем selectedDate если он передан, иначе используем date
  const actualSelectedDate = selectedDate || date;

  console.log("[WeekDays] Props:", { date, selectedDate, actualSelectedDate });

  const weekDays = useMemo(() => {
    console.log("[WeekDays] useMemo recalculating weekDays:", { date, actualSelectedDate });
    
    if (!date) {
      console.log("[WeekDays] useMemo - no date, returning empty");
      return [];
    }

    const parsedDate = Date.parse(date);
    if (isNaN(parsedDate)) {
      console.log("[WeekDays] useMemo - invalid parsedDate");
      return [];
    }

    const currentDate = new Date(parsedDate);
    if (isNaN(currentDate.getTime())) {
      console.log("[WeekDays] useMemo - invalid currentDate");
      return [];
    }

    // Если есть selectedDate и она отличается от date, используем selectedDate как центр
    const centerDate = actualSelectedDate && actualSelectedDate !== date 
      ? new Date(actualSelectedDate)
      : currentDate;
    
    if (isNaN(centerDate.getTime())) {
      console.log("[WeekDays] useMemo - invalid centerDate");
      return [];
    }

    const startDate = new Date(centerDate);
    startDate.setDate(centerDate.getDate() - 3);

    if (isNaN(startDate.getTime())) {
      console.log("[WeekDays] useMemo - invalid startDate");
      return [];
    }

    const days = [];
    // Используем date-fns для правильного форматирования даты в формате yyyy-MM-dd
    const selectedDateString = actualSelectedDate ? format(new Date(actualSelectedDate), "yyyy-MM-dd") : null;
    const addedSelectedDate = new Set<string>();

    console.log("[WeekDays] useMemo - selectedDateString:", selectedDateString);

    // Добавляем выбранную дату, если она не попадает в диапазон
    if (selectedDateString) {
      const selectedDateObj = new Date(selectedDateString);
      if (!isNaN(selectedDateObj.getTime())) {
        const selectedDayDate = new Date(selectedDateObj);
        selectedDayDate.setHours(0, 0, 0, 0);
        
        const startDayDate = new Date(startDate);
        startDayDate.setHours(0, 0, 0, 0);
        
        const endDayDate = new Date(startDate);
        endDayDate.setDate(startDate.getDate() + 6);
        endDayDate.setHours(0, 0, 0, 0);
        
        const selectedTimestamp = selectedDayDate.getTime();
        const startTimestamp = startDayDate.getTime();
        const endTimestamp = endDayDate.getTime();
        
        console.log("[WeekDays] useMemo - date range check:", {
          selectedTimestamp,
          startTimestamp,
          endTimestamp,
          isOutOfRange: selectedTimestamp < startTimestamp || selectedTimestamp > endTimestamp
        });
        
        if (selectedTimestamp < startTimestamp || selectedTimestamp > endTimestamp) {
          const dayDate = new Date(selectedDateObj);
          dayDate.setHours(0, 0, 0, 0);
          const nowDate = new Date();
          nowDate.setHours(0, 0, 0, 0);
          const disableDate = dayDate.getTime() < nowDate.getTime();
          
          console.log("[WeekDays] useMemo - adding selected date outside range:", selectedDateString);
          
          days.push({
            dateString: selectedDateString,
            dayName: dateFormat(selectedDateObj, "ddd"),
            formattedDate: dateFormat(selectedDateObj, "dd.mm.yyyy"),
            textDateName: dateFormat(selectedDateObj, "ddd"),
            dayNumber: dateFormat(selectedDateObj, "dd"),
            disabled: disableDate,
          });
          addedSelectedDate.add(selectedDateString);
        }
      }
    }

    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);

      if (isNaN(day.getTime())) {
        continue;
      }

      const dayDateString = format(day, "yyyy-MM-dd");
      
      // Пропускаем, если эта дата уже добавлена как выбранная
      if (addedSelectedDate.has(dayDateString)) {
        continue;
      }

      const dayDate = new Date(day).setHours(0, 0, 0, 0);
      const nowDate = new Date().setHours(0, 0, 0, 0);
      const disableDate = dayDate < nowDate;

      days.push({
        dateString: dayDateString,
        dayName: dateFormat(day, "ddd"), // Сокращенное название дня недели
        formattedDate: dateFormat(day, "dd.mm.yyyy"),
        textDateName: dateFormat(day, "ddd"), // Сокращенное название дня недели
        dayNumber: dateFormat(day, "dd"), // Только число дня месяца
        disabled: disableDate,
      });
    }

    // Сортируем дни по дате
    days.sort((a, b) => {
      const dateA = new Date(a.dateString).getTime();
      const dateB = new Date(b.dateString).getTime();
      return dateA - dateB;
    });

    console.log("[WeekDays] useMemo - final days:", days.map(d => ({ dateString: d.dateString, disabled: d.disabled })));

    return days;
  }, [date, actualSelectedDate]);

  useEffect(() => {
    if (selectedDayRef.current && containerRef.current) {
      const container = containerRef.current;
      const selectedButton = selectedDayRef.current;

      const containerWidth = container.offsetWidth;
      const buttonLeft = selectedButton.offsetLeft;
      const buttonWidth = selectedButton.offsetWidth;
      const buttonCenter = buttonLeft + buttonWidth / 2;
      const scrollLeft = buttonCenter - containerWidth / 2;

      container.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }
  }, [actualSelectedDate, weekDays]);

  const isSelected = (dayDate: string) => {
    if (!actualSelectedDate || !dayDate) {
      console.log("[WeekDays] isSelected - false (empty):", { actualSelectedDate, dayDate });
      return false;
    }
    // Нормализуем обе даты для сравнения (формат yyyy-MM-dd)
    const normalizedDate = actualSelectedDate.trim();
    const normalizedDayDate = dayDate.trim();
    const isMatch = normalizedDate === normalizedDayDate;
    if (isMatch) {
      console.log("[WeekDays] isSelected - MATCH:", { normalizedDate, normalizedDayDate });
    }
    return isMatch;
  };

  if (!weekDays || weekDays.length === 0) {
    return null;
  }

  return (
    <div className={styles.weekDays} ref={containerRef}>
      {weekDays.map((day) => (
        <button
          key={day.dateString}
          ref={isSelected(day.dateString) ? selectedDayRef : null}
          className={`${styles.weekDay} ${day.disabled ? styles.weekDay_disabled : ""}`}
          onClick={() => {
            console.log("[WeekDays] Day clicked:", day.dateString);
            onDayClick(day.dateString);
          }}
          disabled={day.disabled}
        >
          <span className={styles.weekDay_textDateName}>
            {day.textDateName}
          </span>
          <span
            className={`${styles.weekDay_number} ${
              isSelected(day.dateString) ? styles.weekDay_number_selected : ""
            }`}
          >
            {day.dayNumber}
          </span>
        </button>
      ))}
    </div>
  );
};
